export type BookingService = {
  id: string;
  title: string;
  description: string;
  basePrice: number;
  duration: string;
  icon: string;
};

export const bookingServices: BookingService[] = [
  {
    id: "standard",
    title: "Standard Clean",
    description: "Kitchen, bath, living areas. Dust, mop, vacuum, trash.",
    basePrice: 149,
    duration: "≈ 3–4 hrs",
    icon: "ri-home-smile-line",
  },
  {
    id: "deep",
    title: "Deep Clean",
    description: "Everything in Standard plus baseboards, inside oven & fridge.",
    basePrice: 249,
    duration: "≈ 6–7 hrs",
    icon: "ri-sparkling-2-line",
  },
  {
    id: "move",
    title: "Move-Out Clean",
    description: "Empty home reset for landlords, buyers, or fresh starts.",
    basePrice: 289,
    duration: "≈ 6–8 hrs",
    icon: "ri-truck-line",
  },
  {
    id: "airbnb",
    title: "Airbnb Turnover",
    description: "Fast linen reset, restock and refresh between guests.",
    basePrice: 119,
    duration: "≈ 2–3 hrs",
    icon: "ri-key-2-line",
  },
];

export const frequencyOptions = [
  { id: "once", label: "One-time", discount: 0, sublabel: "Pay full price" },
  { id: "monthly", label: "Monthly", discount: 0.05, sublabel: "Save 5%" },
  { id: "biweekly", label: "Bi-weekly", discount: 0.1, sublabel: "Save 10%" },
  { id: "weekly", label: "Weekly", discount: 0.15, sublabel: "Save 15% · most popular" },
] as const;

export const bedroomOptions = ["Studio", "1 BR", "2 BR", "3 BR", "4 BR", "5+ BR"];
export const bathroomOptions = ["1", "1.5", "2", "2.5", "3", "3.5+"];

export const addOns = [
  { id: "laundry", label: "Laundry & folding", price: 25, icon: "ri-shirt-line" },
  { id: "fridge", label: "Inside fridge", price: 30, icon: "ri-fridge-line" },
  { id: "oven", label: "Inside oven", price: 35, icon: "ri-fire-line" },
  { id: "windows", label: "Interior windows", price: 40, icon: "ri-window-line" },
  { id: "cabinets", label: "Inside cabinets", price: 30, icon: "ri-archive-line" },
  { id: "pets", label: "Pet-home extra dust", price: 20, icon: "ri-emotion-happy-line" },
];

export const timeSlots = [
  "8:00 AM",
  "9:30 AM",
  "11:00 AM",
  "12:30 PM",
  "2:00 PM",
  "3:30 PM",
  "5:00 PM",
];

export type BookingCleaner = {
  id: string;
  name: string;
  role: string;
  rate: number;
  rating: number;
  jobs: number;
  bio: string;
  highlights: string[];
  available: string[];
  image: string;
};

export const bookingCleaners: BookingCleaner[] = [
  {
    id: "maya",
    name: "Maya Olsen",
    role: "Senior Cleaner",
    rate: 32,
    rating: 4.96,
    jobs: 612,
    bio: "Six years with PureNest. Lives for a perfectly streak-free kitchen.",
    highlights: ["Eco products", "Pet-friendly", "English · Norwegian"],
    available: ["8:00 AM", "11:00 AM", "2:00 PM"],
    image:
      "https://readdy.ai/api/search-image?query=Friendly%20professional%20female%20house%20cleaner%20in%20her%2030s%20smiling%20warmly%20wearing%20clean%20olive%20green%20apron%2C%20soft%20cream%20studio%20background%2C%20editorial%20portrait%20photography%20natural%20warm%20light%20approachable%20trustworthy&width=600&height=720&seq=book-cleaner-01&orientation=portrait",
  },
  {
    id: "daniel",
    name: "Daniel Reyes",
    role: "Deep-Clean Specialist",
    rate: 36,
    rating: 4.92,
    jobs: 438,
    bio: "Detail obsessive. Has been known to send before/after photos unprompted.",
    highlights: ["Deep-clean certified", "Allergen-safe", "English · Spanish"],
    available: ["9:30 AM", "12:30 PM", "3:30 PM"],
    image:
      "https://readdy.ai/api/search-image?query=Confident%20professional%20male%20house%20cleaner%20in%20his%20late%2020s%20smiling%20kindly%20wearing%20clean%20beige%20uniform%2C%20soft%20cream%20studio%20background%2C%20editorial%20portrait%20photography%20natural%20warm%20light%20friendly%20reliable&width=600&height=720&seq=book-cleaner-02&orientation=portrait",
  },
  {
    id: "aisha",
    name: "Aisha Bennett",
    role: "Eco Clean Lead",
    rate: 34,
    rating: 5.0,
    jobs: 521,
    bio: "Plant-based products only. Will quietly reorganize your spice rack.",
    highlights: ["100% eco", "Plant-based", "English"],
    available: ["8:00 AM", "11:00 AM", "12:30 PM", "5:00 PM"],
    image:
      "https://readdy.ai/api/search-image?query=Friendly%20professional%20female%20house%20cleaner%20in%20her%20late%2020s%20warm%20smile%20wearing%20clean%20sage%20green%20apron%2C%20soft%20cream%20studio%20background%2C%20editorial%20portrait%20photography%20natural%20warm%20light%20approachable&width=600&height=720&seq=book-cleaner-03&orientation=portrait",
  },
  {
    id: "liam",
    name: "Liam Hartwell",
    role: "Move-Out Pro",
    rate: 30,
    rating: 4.88,
    jobs: 287,
    bio: "Apartment turnover wizard. Carries his own ladder. Always early.",
    highlights: ["Move-out specialist", "Heavy lift OK", "English"],
    available: ["9:30 AM", "2:00 PM", "3:30 PM", "5:00 PM"],
    image:
      "https://readdy.ai/api/search-image?query=Professional%20male%20house%20cleaner%20in%20his%20early%2030s%20gentle%20smile%20wearing%20clean%20cream%20uniform%2C%20soft%20warm%20studio%20background%2C%20editorial%20portrait%20photography%20natural%20directional%20light%20friendly%20trustworthy&width=600&height=720&seq=book-cleaner-04&orientation=portrait",
  },
  {
    id: "sofia",
    name: "Sofia Martinez",
    role: "Family Home Specialist",
    rate: 33,
    rating: 4.94,
    jobs: 489,
    bio: "Kid-rooms, pet hair, sticky surfaces — nothing fazes her.",
    highlights: ["Family-safe products", "Pet-friendly", "English · Spanish"],
    available: ["11:00 AM", "2:00 PM", "3:30 PM"],
    image:
      "https://readdy.ai/api/search-image?query=Warm%20friendly%20female%20house%20cleaner%20in%20her%20mid%2030s%20with%20kind%20smile%20wearing%20clean%20cream%20apron%2C%20soft%20warm%20studio%20background%2C%20editorial%20portrait%20photography%20natural%20light%20approachable%20trustworthy&width=600&height=720&seq=book-cleaner-05&orientation=portrait",
  },
  {
    id: "jonah",
    name: "Jonah Park",
    role: "Studio & Airbnb Pro",
    rate: 31,
    rating: 4.9,
    jobs: 356,
    bio: "Fast, quiet, surgical. Hosts love him. Lives for tight turnovers.",
    highlights: ["Fast turnover", "Linen reset", "English · Korean"],
    available: ["8:00 AM", "9:30 AM", "12:30 PM"],
    image:
      "https://readdy.ai/api/search-image?query=Professional%20male%20house%20cleaner%20in%20his%20late%2020s%20warm%20friendly%20smile%20wearing%20clean%20olive%20uniform%2C%20soft%20warm%20studio%20background%2C%20editorial%20portrait%20photography%20natural%20light%20approachable&width=600&height=720&seq=book-cleaner-06&orientation=portrait",
  },
];